// admin/components/TicketList.jsx
// ─────────────────────────────────────────────────────────────
// REWIRED — renders Conversation objects now, not SupportTicket
// objects. Key field changes:
//   ticket.ticketId    → conversation._id (no separate ticketId field
//     exists anymore — using the Mongo _id directly, truncated for
//     display, matching what LiveAgentChat.jsx's header now shows)
//   ticket.status "pending"/"assigned"/"resolved"
//     → conversation.status "waiting_for_agent"/"with_agent"/"closed"
//   ticket.category / ticket.description
//     → conversation.escalation?.category / conversation.escalation?.unresolvedQuestion
//     (a waiting_for_agent conversation ALWAYS has escalation
//     populated — that's what put it in this list in the first place
//     — but the optional chaining stays as a defensive guard, e.g. for
//     a "mine" tab conversation an agent claimed directly without an
//     AI escalation ever happening, if that path gets added later)
//
// Search now matches against the truncated id, escalation category,
// and unresolved question — the closest equivalents to the old
// ticketId/category/description search targets.
// ─────────────────────────────────────────────────────────────
import { useState } from "react";

function TicketList({ tickets, selectedTicket, onAccept, activeTab }) {
  const [search, setSearch] = useState("");

  const filtered = tickets.filter(c =>
    c._id?.toLowerCase().includes(search.toLowerCase()) ||
    c.escalation?.category?.toLowerCase().includes(search.toLowerCase()) ||
    c.escalation?.unresolvedQuestion?.toLowerCase().includes(search.toLowerCase()) ||
    c.customer?.displayName?.toLowerCase().includes(search.toLowerCase()) ||
    c.customer?.email?.toLowerCase().includes(search.toLowerCase())
  );

  const emptyLabel = activeTab.replace("_", " ");

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Search */}
      <div className="p-4 border-b border-white/10 flex-shrink-0">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25"
            fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search conversations…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00C896]/50 transition-colors"
          />
          {search && (
            <button onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-12 text-center px-4">
            <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center mb-3">
              <svg className="w-5 h-5 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-white/30 text-sm">
              {search.trim() ? "No results found" : `No ${emptyLabel} conversations`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/8">
            {filtered.map(convo => {
              const isSelected = selectedTicket?._id === convo._id;
              const isWaiting  = convo.status === "waiting_for_agent";

              return (
                <div
                  key={convo._id}
                  className={`p-4 sm:p-5 transition-colors ${
                    isSelected ? "bg-[#00C896]/8 border-l-2 border-[#00C896]" : "hover:bg-white/4"
                  }`}
                >
                  {/* Top row: short id + status + time */}
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="font-mono text-[11px] text-white/40">#{convo._id?.slice(-8)}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                      convo.status === "waiting_for_agent" ? "bg-amber-400/15 text-amber-400"
                      : convo.status === "with_agent"        ? "bg-blue-400/15 text-blue-400"
                      : "bg-emerald-400/15 text-emerald-400"
                    }`}>
                      {convo.status === "waiting_for_agent" ? "Waiting"
                      : convo.status === "with_agent"        ? "In Progress"
                      : "Closed"}
                    </span>
                    <span className="ml-auto text-[11px] text-white/35 flex-shrink-0">
                      {convo.status === "closed" && convo.closedAt
                        ? new Date(convo.closedAt).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
                        : convo.escalation?.triggeredAt
                          ? new Date(convo.escalation.triggeredAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                          : convo.createdAt
                            ? new Date(convo.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                            : ""}
                    </span>
                  </div>

                  {/* FIXED — customer identity, so an agent scanning
                      the list knows WHO each conversation is with,
                      not just an opaque conversation id. Falls back
                      to a truncated raw userId if no User record was
                      found (e.g. lookup failed, or the identity
                      enrichment hasn't been deployed against a
                      matching User schema yet — see
                      socketHandlers.js's getUserIdentity). */}
                  <p className="text-[13px] font-medium text-white/80 mb-1 truncate">
                    {convo.customer?.displayName || convo.customer?.email || `User ${convo.userId?.slice(-8) || ""}`}
                  </p>

                  {/* Category + the AI's unresolved question */}
                  <p className="font-semibold text-white text-sm mb-1 line-clamp-1 capitalize">
                    {convo.escalation?.category || "General"}
                  </p>
                  <p className="text-xs text-white/50 line-clamp-2 mb-3">
                    {convo.escalation?.unresolvedQuestion || "No summary available."}
                  </p>

                  {/* Action button */}
                  {isWaiting ? (
                    <button
                      onClick={() => onAccept(convo)}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        isSelected
                          ? "bg-[#00C896]/20 text-[#00C896] border border-[#00C896]/30 cursor-default"
                          : "bg-[#00C896] hover:bg-[#00b386] text-black active:scale-[0.98]"
                      }`}
                    >
                      {isSelected ? "✓ Joined Chat" : "Accept & Join Chat"}
                    </button>
                  ) : (
                    <button
                      onClick={() => onAccept(convo)}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        isSelected
                          ? "bg-white/15 text-white border border-white/20 cursor-default"
                          : "bg-white/8 hover:bg-white/15 text-white/70 hover:text-white active:scale-[0.98]"
                      }`}
                    >
                      {isSelected ? "✓ Viewing Chat" : "Open Chat"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default TicketList;
